/**
 * Настраиваемые цвета: статусы заказов, «Финансы», сотрудники.
 *
 * Тема оформления меняет только акцентную пару (кнопки, меню, шапки). Смысловые
 * цвета — «готово», «не оплачен», «ожидание» — она не трогает нарочно: зелёный
 * обязан значить «готово» при любой теме. Но САМИ оттенки владелец вправе
 * подобрать под себя, и для этого они вынесены в настройки лаборатории.
 *
 * В базе хранятся ТОЛЬКО ОТЛИЧИЯ от стандартных. Пустой объект — «всё по
 * умолчанию»; стандартный цвет, поправленный в программе, приедет всем без
 * миграции данных, если владелец его не переопределял.
 *
 * Модуль общий для сервера и браузера: сервер проверяет им присланное, браузер
 * — красит. Разные правила проверки в двух местах однажды разошлись бы, и
 * сервер принял бы то, что программа не умеет нарисовать.
 */
import { resolveThemeName, themeColors } from './theme-colors.js';
// ---------------------------------------------------------------------------
// Наборы и стандартные значения
// ---------------------------------------------------------------------------
export const STATUS_COLOR_KEYS = ['done', 'normal', 'hold', 'urgent', 'critical', 'danger', 'warning'];
/**
 * Стандартные цвета статусов.
 *
 * Подобраны с проверкой палитры на белом фоне: пары, которые стоят в одной
 * строке журнала, различимы и при нарушениях цветоощущения. Меняя значение
 * здесь, прогоняй проверку снова — на глаз такое не решается.
 */
export const DEFAULT_STATUS_COLORS = {
    done: '#2C8A5A',
    normal: '#D9AE3A',
    hold: '#2656A6',
    urgent: '#D98A7E',
    critical: '#962234',
    danger: '#D24A26',
    warning: '#AE5E1E',
};
export const FINANCE_COLOR_KEYS = ['revenue', 'materials', 'rent', 'tax', 'other', 'salaries', 'owner'];
/**
 * Стандартные цвета «Финансов» — те, что стояли до настройки.
 *
 * Раньше они были взяты у статусов (золотые «Материалы» — это «в работе»,
 * зелёное «Остаётся вам» — «готово»). Теперь это отдельные значения: иначе
 * перекраска плашки «В работе» молча перекрашивала бы и материалы в отчёте.
 */
export const DEFAULT_FINANCE_COLORS = {
    revenue: '#52525B',
    materials: '#D9AE3A',
    rent: '#AE5E1E',
    tax: '#D24A26',
    other: '#6E6A61',
    salaries: '#D98A7E',
    owner: '#2C8A5A',
};
/**
 * Набор, из которого новому сотруднику достаётся цвет — первый свободный.
 *
 * Первые два — те, что стояли у техников до настройки: тёмно-серый и золото.
 * Дальше — различимые между собой тона, чтобы столбики на графике выплат не
 * сливались, сколько бы людей ни было.
 */
export const EMPLOYEE_PALETTE = [
    '#52525B',
    '#D9AE3A',
    '#D98A7E',
    '#2C8A5A',
    '#2656A6',
    '#7A5CA8',
    '#1F7A8C',
    '#AE5E1E',
];
/** Готовые цвета для выбора в одно касание. */
export const COLOR_PRESETS = [
    '#2C8A5A', '#D9AE3A', '#2656A6', '#D98A7E', '#962234', '#D24A26', '#AE5E1E', '#52525B',
    '#1F7A8C', '#7A5CA8', '#B8559A', '#4C5578', '#8A9A5B', '#C07A45', '#3A6FC4', '#9C2F2F',
];
// ---------------------------------------------------------------------------
// HEX и RGB
// ---------------------------------------------------------------------------
/**
 * Пользовательский ввод → `#RRGGBB` заглавными; `null` — ввод неполон.
 *
 * Принимается и короткая запись `#abc`, и без решётки: код цвета копируют
 * откуда угодно, и решётку то теряют, то нет.
 */
export function normalizeHex(input) {
    const value = input.trim().replace(/^#/, '');
    if (/^[\da-f]{6}$/i.test(value))
        return `#${value.toUpperCase()}`;
    if (/^[\da-f]{3}$/i.test(value)) {
        return `#${value.split('').map((c) => c + c).join('').toUpperCase()}`;
    }
    return null;
}
/** `#RRGGBB` → каналы. Некорректное значение даёт чёрный, а не падение. */
export function hexToRgb(hex) {
    const normalized = normalizeHex(hex);
    if (!normalized)
        return { r: 0, g: 0, b: 0 };
    const value = Number.parseInt(normalized.slice(1), 16);
    return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}
/** Каналы → `#RRGGBB`. Лишнее обрезается до 0…255, дробное округляется. */
export function rgbToHex(r, g, b) {
    const clamp = (v) => Math.max(0, Math.min(255, Math.round(v || 0)));
    return `#${[clamp(r), clamp(g), clamp(b)].map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase()}`;
}
/**
 * HSV → HEX. Нужен квадрату выбора: по горизонтали насыщенность, по
 * вертикали яркость, оттенок — отдельной полосой.
 */
export function hsvToHex(h, s, v) {
    const hue = ((h % 360) + 360) % 360;
    const c = v * s;
    const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
    const m = v - c;
    const [r, g, b] = hue < 60 ? [c, x, 0]
        : hue < 120 ? [x, c, 0]
            : hue < 180 ? [0, c, x]
                : hue < 240 ? [0, x, c]
                    : hue < 300 ? [x, 0, c]
                        : [c, 0, x];
    return rgbToHex((r + m) * 255, (g + m) * 255, (b + m) * 255);
}
/** HEX → HSV: откуда поставить точку в квадрате и на полосе оттенка. */
export function hexToHsv(hex) {
    const { r, g, b } = hexToRgb(hex);
    const [R, G, B] = [r / 255, g / 255, b / 255];
    const max = Math.max(R, G, B);
    const min = Math.min(R, G, B);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === R)
            h = 60 * (((G - B) / d) % 6);
        else if (max === G)
            h = 60 * ((B - R) / d + 2);
        else
            h = 60 * ((R - G) / d + 4);
    }
    return { h: (h + 360) % 360, s: max === 0 ? 0 : d / max, v: max };
}
// ---------------------------------------------------------------------------
// Различимость и подпись
// ---------------------------------------------------------------------------
const linear = (channel) => {
    const c = channel / 255;
    return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
function oklab(hex) {
    const { r, g, b } = hexToRgb(hex);
    const [R, G, B] = [linear(r), linear(g), linear(b)];
    const l = Math.cbrt(0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B);
    const m = Math.cbrt(0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B);
    const s = Math.cbrt(0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B);
    return [
        0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
        1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
        0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
    ];
}
/**
 * Насколько различаются два цвета для глаза — расстояние в OKLab, ×100.
 *
 * Меньше 15 — две плашки рядом уже путают даже при полном цветовом зрении;
 * этот же порог стоит в проверке палитры, по которой подбирались стандартные.
 */
export function colorDistance(a, b) {
    const x = oklab(a);
    const y = oklab(b);
    return Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]) * 100;
}
/** Порог, ниже которого цвета считаются похожими. */
export const SIMILAR_COLOR_DISTANCE = 15;
/**
 * Цвет подписи на заливке: белый, пока читается, иначе почти чёрный.
 *
 * Порог 2:1, а не строгие 4,5:1: подпись на плашке жирная и короткая, а
 * по строгому правилу белый пришлось бы убрать даже с нынешнего золота «В
 * работе» (2,1:1), где владелец выбрал его сам и где его прекрасно видно.
 * Тёмная подпись нужна там, где белая по-настоящему пропадает: на жёлтом,
 * светло-зелёном, бледных тонах.
 */
export function textOnColor(hex) {
    const { r, g, b } = hexToRgb(hex);
    const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
    return 1.05 / (luminance + 0.05) >= 2 ? '#FFFFFF' : '#1C1B18';
}
// ---------------------------------------------------------------------------
// Разбор хранимого и присланного
// ---------------------------------------------------------------------------
function pickColors(raw, keys) {
    const result = {};
    if (!raw || typeof raw !== 'object')
        return result;
    for (const key of keys) {
        const value = raw[key];
        const hex = typeof value === 'string' ? normalizeHex(value) : null;
        if (hex)
            result[key] = hex;
    }
    return result;
}
/**
 * Хранимые настройки → годные к употреблению. Мягко: негодное отбрасывается.
 *
 * Для ЧТЕНИЯ. Испорченная запись в базе не должна ронять запуск программы —
 * на её месте просто встанет стандартный цвет.
 */
export function readColorSettings(raw) {
    const source = raw && typeof raw === 'object' ? raw : {};
    const employees = {};
    if (source.employees && typeof source.employees === 'object') {
        for (const [id, value] of Object.entries(source.employees)) {
            const hex = typeof value === 'string' ? normalizeHex(value) : null;
            if (hex && id)
                employees[id] = hex;
        }
    }
    return {
        status: pickColors(source.status, STATUS_COLOR_KEYS),
        finance: pickColors(source.finance, FINANCE_COLOR_KEYS),
        employees,
    };
}
/**
 * Присланные настройки → проверенные. Строго: ошибка называется словами.
 *
 * Для ЗАПИСИ. Молча выбросить неверный код цвета значило бы, что владелец
 * нажал «Сохранить», увидел «сохранено», а цвет остался прежним.
 */
export function parseColorSettings(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return { ok: false, error: 'Настройки цветов пришли в неверном виде' };
    }
    const source = raw;
    const check = (group, name, keys) => {
        if (group === undefined)
            return null;
        if (!group || typeof group !== 'object' || Array.isArray(group)) {
            return `Раздел цветов «${name}» пришёл в неверном виде`;
        }
        for (const [key, value] of Object.entries(group)) {
            if (keys && !keys.includes(key))
                return `Неизвестный цвет «${key}» в разделе «${name}»`;
            if (typeof value !== 'string' || !normalizeHex(value)) {
                return `Цвет «${key}» задан неверно: нужен код вида #2C8A5A`;
            }
        }
        return null;
    };
    const error = check(source.status, 'Статусы заказов', STATUS_COLOR_KEYS) ??
        check(source.finance, 'Финансы', FINANCE_COLOR_KEYS) ??
        check(source.employees, 'Сотрудники');
    if (error)
        return { ok: false, error };
    return { ok: true, value: readColorSettings(source) };
}
// ---------------------------------------------------------------------------
// Итоговые цвета
// ---------------------------------------------------------------------------
/**
 * Пара своей темы из хранимого: только обе половины и только годные коды.
 *
 * С одним цветом меню или кнопки остались бы без цвета, поэтому половина
 * пары — всё равно что никакой.
 */
export function readThemePair(raw) {
    if (!raw || typeof raw !== 'object')
        return null;
    const source = raw;
    const accent = typeof source.accent === 'string' ? normalizeHex(source.accent) : null;
    const accentDark = typeof source.accentDark === 'string' ? normalizeHex(source.accentDark) : null;
    return accent && accentDark ? { accent, accentDark } : null;
}
/**
 * Цвета темы с учётом своей: для `custom` — пара пользователя, иначе — готовая.
 *
 * Одна функция на программу и сервер: манифест приложения красит ей строку
 * заголовка окна, программа — меню и кнопки. Считай они по-разному, окно
 * открывалось бы одним цветом, а внутри было бы другое.
 */
export function resolveThemePair(themeName, pair) {
    if (resolveThemeName(themeName) === 'custom' && pair)
        return pair;
    return themeColors(themeName);
}
/**
 * Тёмный тон по основному — для кнопки «Подобрать сам».
 *
 * Тот же оттенок, но на треть темнее: так устроены пары готовых тем, где
 * меню — это кнопка в тени. Насыщенность чуть прибавляется: при затемнении
 * цвет иначе сереет и перестаёт узнаваться как «тот же, только темнее».
 */
export function darkerShade(hex) {
    const { h, s: sat, v } = hexToHsv(hex);
    return hsvToHex(h, Math.min(1, sat * 1.1), v * 0.68);
}
export function resolveStatusColors(settings) {
    return { ...DEFAULT_STATUS_COLORS, ...(settings?.status ?? {}) };
}
export function resolveFinanceColors(settings) {
    return { ...DEFAULT_FINANCE_COLORS, ...(settings?.finance ?? {}) };
}
/**
 * Первый цвет набора, которого ещё нет у сотрудников.
 *
 * Когда набор кончился, цвета идут по второму кругу: совпадение у двоих
 * лучше, чем отказ завести третьего.
 */
export function nextEmployeeColor(taken) {
    const used = new Set([...taken].map((hex) => normalizeHex(hex) ?? hex));
    const free = EMPLOYEE_PALETTE.find((hex) => !used.has(hex));
    if (free)
        return free;
    return EMPLOYEE_PALETTE[used.size % EMPLOYEE_PALETTE.length];
}
/**
 * Цвета сотрудников: закреплённый — как есть, остальным — по порядку свободные.
 *
 * Незакреплённым цвет раздаётся в переданном порядке, и такой цвет может
 * съехать, если появится кто-то раньше по списку. Поэтому сервер закрепляет
 * цвет при заведении сотрудника, а экран настройки — при сохранении: здесь
 * это только страховка для записей, заведённых до настройки.
 */
export function employeeColors(employeeIds, settings) {
    const pinned = settings?.employees ?? {};
    const result = new Map();
    const taken = employeeIds.map((id) => pinned[id]).filter((hex) => Boolean(hex));
    for (const id of employeeIds) {
        const own = pinned[id];
        if (own) {
            result.set(id, own);
        }
        else {
            const hex = nextEmployeeColor(taken);
            taken.push(hex);
            result.set(id, hex);
        }
    }
    return result;
}
//# sourceMappingURL=color-settings.js.map